import React, { Suspense, lazy, useEffect } from 'react'
import { Routes, Route, useLocation } from "react-router-dom"
import { AnimatePresence } from 'framer-motion'

import Dashboard from '../../pages/Dashboard'
import Delivery from '../../pages/Delivery'
import Login from '../../pages/Login'
import ShuttleService from '../../pages/ShuttleService'
import SignUp from '../../pages/SignUp'
import Trips from '../../pages/Trips'
import Events from '../../pages/Events'
import Rentals from '../../pages/Rentals'
import Shopping from '../../pages/Shopping'
import ChauffeurService from '../../pages/ChauffeurService'
import SpecialArrangements from '../../pages/SpecialArrangements'
import AboutUs from '../../pages/AboutUs'
import Tours from '../../pages/Tours'
import ScrollToTop from '../ScrollToTop'

const ReserveTour = lazy(() => import('../../pages/admin/Reservation/ReserveTour'))
const ReserveRental = lazy(() => import('../../pages/admin/Reservation/ReserveRental'))
const TourManagement = lazy(() => import('../../pages/admin/Tour/TourManagement'))
const CreateTour = lazy(() => import('../../pages/admin/Tour/CreateTour'))
const ViewTour = lazy(() => import('../../pages/admin/Tour/ViewTour'))
const DriverManagement = lazy(() => import('../../pages/admin/Driver/DriverManagement'))
const CreateDriver = lazy(() => import('../../pages/admin/Driver/CreateDriver'))
const LocationManagement = lazy(() => import('../../pages/admin/Location/LocationManagement'))
const CreateLocation = lazy(() => import('../../pages/admin/Location/CreateLocation'))
const RentalManagement = lazy(() => import('../../pages/admin/Rental/RentalManagement'))
const CreateRental = lazy(() => import('../../pages/admin/Rental/CreateRental'))
const GuideManagement = lazy(() => import('../../pages/admin/Guide/GuideManagement'))
const CreateGuide = lazy(() => import('../../pages/admin/Guide/CreateGuide'))
const BusManagement = lazy(() => import('../../pages/admin/Bus/BusManagement'))
const CreateBus = lazy(() => import('../../pages/admin/Bus/CreateBus'))
const Location = lazy(() => import('../../pages/Location'))
const CustomTour = lazy(() => import('../../pages/admin/Tour/CustomTour'))
const ProcessPayment = lazy(() => import('../../pages/payment/ProcessPayment'))
const PaymentSuccess = lazy(() => import('../../pages/payment/PaymentSuccess'))
const PaymentFailed = lazy(() => import('../../pages/payment/PaymentFailed'))
const ReservationManagement = lazy(() => import('../../pages/admin/Reservation/ReservationManagement'))
const MyReservations = lazy(() => import('../../pages/admin/Reservation/MyReservations'))

function RouteFallback() {
  return (
    <div className="d-flex justify-content-center align-items-center min-vh-50 p-5 text-cream" role="status" aria-live="polite">
      Loading…
    </div>
  )
}

function LazyRoute({ element }) {
  return <Suspense fallback={<RouteFallback />}>{element}</Suspense>
}

export default function Router(props) {

  const location = useLocation();

  useEffect(() => {
    document.body.classList.toggle('inner-page', location.pathname !== '/')
  }, [location.pathname])

  return (
    
    <AnimatePresence exitBeforeEnter>
          <ScrollToTop />
          <Routes location={location} key={location.pathname}>
              <Route exact path="/" element={<Dashboard />} />
              <Route path='/about-us' element={<AboutUs />} />
              <Route path='/signup' element={<SignUp />} />
              <Route path='/login' element={<Login />} />
              <Route path='/signup/:path' element={<SignUp />} />
              <Route path='/login/:path' element={<Login />} />
              <Route path='/profile/reservation' element={<LazyRoute element={<MyReservations />} />} />
              <Route path='reservation/:type' element={<LazyRoute element={<ReservationManagement />} />} />
              <Route path='tours' element={<Tours />} />
              <Route path='tours/book' element={<LazyRoute element={<CustomTour />} />} />
              <Route path='tours/edit/:id' element={<LazyRoute element={<CustomTour />} />} />
              <Route path="tours/reserve/:id" element={<LazyRoute element={<ReserveTour />} />}  />
              <Route path="tours/management" element={<LazyRoute element={<TourManagement />} />}  />
              <Route path="tours/management/:id" element={<LazyRoute element={<ViewTour />} />}  />
              <Route path="tours/management/edit/:id" element={<LazyRoute element={<CreateTour />} />}  />
              <Route path="tours/management/create" element={<LazyRoute element={<CreateTour />} />}  />
              <Route path="drivers/management" element={<LazyRoute element={<DriverManagement />} />}  />
              <Route path="drivers/management/create" element={<LazyRoute element={<CreateDriver />} />}  />
              <Route path="drivers/management/edit/:id" element={<LazyRoute element={<CreateDriver />} />}  />
              <Route path="locations/management" element={<LazyRoute element={<LocationManagement />} />}  />
              <Route path="locations/management/create" element={<LazyRoute element={<CreateLocation />} />}  />
              <Route path="locations/management/edit/:id" element={<LazyRoute element={<CreateLocation />} />}  />
              <Route path='location/:displayName/:id' element={<LazyRoute element={<Location />} />} />
              <Route path='location/:id' element={<LazyRoute element={<Location />} />} />
              <Route path='location/:displayName' element={<LazyRoute element={<Location />} />} />
              <Route path="guides/management" element={<LazyRoute element={<GuideManagement />} />}  />
              <Route path="guides/management/create" element={<LazyRoute element={<CreateGuide />} />}  />
              <Route path="guides/management/edit/:id" element={<LazyRoute element={<CreateGuide />} />}  />
              <Route path="buses/management" element={<LazyRoute element={<BusManagement />} />}  />
              <Route path="buses/management/create" element={<LazyRoute element={<CreateBus />} />}  />
              <Route path="buses/management/edit/:id" element={<LazyRoute element={<CreateBus />} />}  />
              <Route path="rental/management" element={<LazyRoute element={<RentalManagement />} />}  />
              <Route path="rental/management/create" element={<LazyRoute element={<CreateRental />} />}  />
              <Route path="rental/management/edit/:id" element={<LazyRoute element={<CreateRental />} />}  />
              <Route path='trips' element={<Trips />} />
              <Route path='events' element={<Events />} />
              <Route path='rentals' element={<Rentals />} />
              <Route path='/rentals/reserve/:id' element={<LazyRoute element={<ReserveRental />} />} />
              <Route path='chauffeuring' element={<ChauffeurService />} />
              <Route path='shuttle' element={<ShuttleService />} />
              <Route path='shopping' element={<Shopping />} />
              <Route path='arrangements' element={<SpecialArrangements />} />
              <Route path='delivery' element={<Delivery />} />
              <Route path='payment/:type/:id' element={<LazyRoute element={<ProcessPayment />} />} />
              <Route path='payment/success/:id' element={<LazyRoute element={<PaymentSuccess />} />} />
              <Route path='payment/failed' element={<LazyRoute element={<PaymentFailed />} />} />
          </Routes>
    </AnimatePresence>
  )
}
